import { AxiosResponse } from "axios";
import httpClient from "../axios/interceptors";
import { handleResponseError } from "helpers/utils";
import { API } from "./endpoint";
import {
  SellerCustomerBase,
  SellerFinanceSummary,
  SellerKpi,
  SellerOrderStatus,
  SellerReportEnvelope,
  SellerReportPeriodInput,
  SellerSalesTrend,
  SellerTodoSummary,
  SellerTopProductList,
} from "./interface/sellerReport.interface";

/**
 * backend ตอบ HTTP 200 เสมอแม้ตอน error (HttpExceptionFilter ฝั่ง api ใช้ response.status(200))
 * axios จึงไม่ throw และ handleResponseError ไม่ทำงาน — ต้องเช็ค status เองตรงนี้
 * ไม่งั้น React Query จะขึ้นว่า success แต่ data เป็น undefined
 */
const unwrap = <T>(res: AxiosResponse<SellerReportEnvelope<T>>): T => {
  const body = res?.data;
  if (!body || body.status === "failed") {
    throw new Error(body?.error_message?.[0] || "ไม่สามารถโหลดข้อมูลได้");
  }
  return body.data as T;
};

/** KPI 4 ช่องบนสุด — ยอดขายสุทธิ / คำสั่งซื้อ / เงินพร้อมถอน / ลูกค้าซื้อซ้ำ */
export const getSellerKpi = async (input: SellerReportPeriodInput): Promise<SellerKpi> => {
  const res: AxiosResponse<SellerReportEnvelope<SellerKpi>> = await httpClient
    .post(API.sellerReportKpi, input)
    .catch(handleResponseError);
  return unwrap(res);
};

/** "สิ่งที่ต้องทำวันนี้" — ยอดสด ไม่ผูกกับช่วงเวลาที่เลือก */
export const getSellerTodoSummary = async (): Promise<SellerTodoSummary> => {
  const res: AxiosResponse<SellerReportEnvelope<SellerTodoSummary>> = await httpClient
    .post(API.sellerReportTodoSummary, {})
    .catch(handleResponseError);
  return unwrap(res);
};

/** กราฟแนวโน้มยอดขาย — points zero-fill และเรียงมาจาก backend แล้ว */
export const getSellerSalesTrend = async (input: SellerReportPeriodInput): Promise<SellerSalesTrend> => {
  const res: AxiosResponse<SellerReportEnvelope<SellerSalesTrend>> = await httpClient
    .post(API.sellerReportSalesTrend, input)
    .catch(handleResponseError);
  return unwrap(res);
};

/** โดนัทสถานะออเดอร์ */
export const getSellerOrderStatus = async (input: SellerReportPeriodInput): Promise<SellerOrderStatus> => {
  const res: AxiosResponse<SellerReportEnvelope<SellerOrderStatus>> = await httpClient
    .post(API.sellerReportOrderStatus, input)
    .catch(handleResponseError);
  return unwrap(res);
};

/** สินค้าขายดี 5 อันดับ — data เป็น array ล้วน */
export const getSellerTopProducts = async (input: SellerReportPeriodInput): Promise<SellerTopProductList> => {
  const res: AxiosResponse<SellerReportEnvelope<SellerTopProductList>> = await httpClient
    .post(API.sellerReportTopProducts, input)
    .catch(handleResponseError);
  return unwrap(res) || [];
};

/** การ์ด "โอกาสซื้อซ้ำ" */
export const getSellerCustomerBase = async (input: SellerReportPeriodInput): Promise<SellerCustomerBase> => {
  const res: AxiosResponse<SellerReportEnvelope<SellerCustomerBase>> = await httpClient
    .post(API.sellerReportCustomerBase, input)
    .catch(handleResponseError);
  return unwrap(res);
};

/** การ์ด "รอบรับเงิน" — ยอดสด ไม่ผูกกับช่วงเวลาที่เลือก */
export const getSellerFinanceSummary = async (): Promise<SellerFinanceSummary> => {
  const res: AxiosResponse<SellerReportEnvelope<SellerFinanceSummary>> = await httpClient
    .post(API.sellerReportFinanceSummary, {})
    .catch(handleResponseError);
  return unwrap(res);
};
